import {IncomeTypes} from '@deliveriesondemand/dod-data';
import {sumTotal} from '@deliveriesondemand/dod-utils';
import {connect} from 'react-redux';
import {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {findDOMNode} from 'react-dom';
import handlers from 'react-handlers';
import injectSheet from 'react-jss';

import Select from '-/components/fields/Select';
import {NumberInput} from '-/components/fields/NumberInput';
import {formatMoney} from 'accounting';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import NavigationService from '-/services/NavigationService';
import {Control, ControlGroup} from '-/components/fields/Control';
import Button from '-/components/general/Button';

const styles = theme => ({
    rentalWarning: {
        backgroundColor: 'white',
        borderColor: theme.colors.primary,
        borderStyle: 'solid',
        borderWidth: 2,
        margin: '20px 0 10px 0',
        padding: 10
    }
});

class AdditionalIncomeItem extends Component {
    constructor(props) {
        super(props);

        this.fieldRefs = {};
    }
    
    getErrors(label = '') {
        const {typeSelect, amount} = this.fieldRefs;
        let errs = amount.getErrors(`${label}, amount`);

        errs = [...errs, ...typeSelect.getErrors(`${label}, type`)];
        return errs;
    }

    render() {
        const {
            jssClass = {},
            type,
            index,
            addText,
            amount,
            description,
            monthsWorked,
            onAddAdditionalIncome,
            onDescriptionChange,
            onIncomeTypeChange,
            onIncomeAmountChange,
            onItemDelete,
            onMonthChange,
            onYearChange,
            showAddButton,
            showDeleteButton,
            typeFilter = () => true,
            yearsWorked
        } = this.props;

        const blacklist = ['other'];
        const types = Object.keys(IncomeTypes)
            .filter(type => !blacklist.includes(type))
            .filter(typeFilter);
        const labelWidth = 200;

        return (
            <ControlGroup labelWidth={labelWidth}>
                <Control>
                    <Select
                        label="Type"
                        innerRef={ref => this.fieldRefs.typeSelect = ref} onChange={(value) => onIncomeTypeChange(index, value)}>

                        <option omitFromList>Please select an income type</option>
                        {types.map((iType, i) => {
                            return (
                                <option
                                    key={i}
                                    selected={iType === type}
                                    value={iType}>
                                    {IncomeTypes[iType]}
                                </option>
                            );
                        })}
                    </Select>
                </Control>
                <Control>
                    <NumberInput
                        ref={ref => this.fieldRefs.amount = ref}
                        label="Monthly Income"
                        placeholder="$XXX"
                        value={amount}
                        onChange={e => onIncomeAmountChange(index, e)} />
                </Control>
                {['additionalJob', 'other'].includes(type) && (
                    <Control>
                        <textarea
                            ref={ref => this.fieldRefs.description = ref}
                            label="Description"
                            placeholder="Enter a Description Here..."
                            value={description}
                            onChange={e => onDescriptionChange(index, e)} />
                    </Control>
                )}
                {type === 'additionalJob' && (
                    <Fragment>
                        <Control labelWidth={labelWidth}>
                            <NumberInput
                                ref={ref => this.fieldRefs.yearsWorked = ref}
                                label="Years Worked"
                                placeholder="X Years"
                                value={yearsWorked}
                                onChange={e => onYearChange(index, e)} />
                        </Control>
                        <Control labelWidth={labelWidth}>
                            <NumberInput
                                ref={ref => this.fieldRefs.monthsWorked = ref}
                                label="Months Worked"
                                placeholder="X Months"
                                value={monthsWorked}
                                onChange={e => onMonthChange(index, e)} />
                        </Control>
                    </Fragment>
                )}
                <div className={jssClass.rentalWarning}>
                    Additional income from your partner is only applicable if they are also on the loan.
                </div>
                {type === 'rental' && (
                    <div className={jssClass.rentalWarning}>
                        You will need to be able to legally prove rental income if you
                        wish to claim it as part of your earnings (i.e. rental agreement
                        or taxes)
                    </div>
                )}
                <div>
                    {showAddButton && (
                        <Button btnStyle="add" onClick={onAddAdditionalIncome}>{addText}</Button>
                    )}
                    {showDeleteButton && (
                        <Button btnStyle="delete" onClick={() => onItemDelete(index)}>
                            <i className="fa fa-close" /> Delete
                        </Button>
                    )}
                </div>
            </ControlGroup>
        );
    }
}

class AdditionalIncomeSection extends Component {
    static propTypes = {
        addText: PropTypes.string,
        mode: PropTypes.string.isRequired,
        onInputChange: PropTypes.func.isRequired,
        typeFilter: PropTypes.func
    };

    static defaultProps = {
        addText: 'Add More Income',
        mode: 'additionalIncome'
    };

    constructor(props) {
        super(props);

        this.onAddAdditionalIncome = this.onAddAdditionalIncome.bind(this);
    }

    componentDidMount() {
        const {entries = []} = this.props;

        if (entries.length === 0) {
            this.onAddAdditionalIncome();
        }
    }

    getRef(i) {
        return `item${i}`;
    }

    onAddAdditionalIncome() {
        const {onItemAdd, entries = []} = this.props;

        onItemAdd();
        setTimeout(() => {
            const ln = entries.length;
            if (ln) {
                const ref = this.getRef(ln - 1);
                const item = this.refs[ref];
                const dom = findDOMNode(item);
                if (dom) {
                    document.body.scrollTop = dom.offsetTop;
                }
            }
        }, 300);
    }

    render() {
        const {
            addText,
            classes = {},
            entries = [],
            mode,
            onItemDelete,
            onIncomeTypeChange,
            onIncomeAmountChange,
            onDescriptionChange,
            onInputChange,
            onMonthChange,
            onYearChange,
            totalText = 'Total Additional Income',
            typeFilter
        } = this.props;

        const total = sumTotal(entries);

        return (
            <div>
                {entries.map((income, i) => {
                    const index = AdditionalIncomeService.getIncomeIndex(income);

                    return (
                        <AdditionalIncomeItem
                            jssClass={classes}
                            ref={this.getRef(i)}
                            key={`additional-income-${index}-${i}`}
                            index={index}
                            onItemDelete={handlers(onItemDelete, onInputChange)}
                            onIncomeTypeChange={handlers(onIncomeTypeChange, onInputChange)}
                            onIncomeAmountChange={handlers(onIncomeAmountChange, onInputChange)}
                            onDescriptionChange={handlers(onDescriptionChange, onInputChange)}
                            onMonthChange={handlers(onMonthChange, onInputChange)}
                            onYearChange={handlers(onYearChange, onInputChange)}
                            onAddAdditionalIncome={this.onAddAdditionalIncome}
                            {...income}
                            addText={addText}
                            showAddButton={i === entries.length - 1}
                            showDeleteButton={(i > 0 || mode !== 'employment')}
                            typeFilter={typeFilter} />
                    );
                })}

                {total !== 0 ? (
                    <p className="info"><strong>{totalText}:</strong> {formatMoney(total)}</p>
                ) : null}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const {mode} = ownProps;
    const employmentInfo = EmploymentInfoService.getEmploymentInfoForCurrentApplicant();
    const {status} = employmentInfo;
    const retirementKeys = ['retirementSocialSecurity', 'retirementPension'];

    let {entries = []} = AdditionalIncomeService.getAdditionalIncomeForCurrentApplicant();

    if (mode === 'employment') {
        entries = entries.slice().filter(item => [...retirementKeys, ''].includes(item.type));
    } else if (status === 'retired') {
        entries = entries.slice().filter(item => !retirementKeys.includes(item.type));
    }

    return {
        entries,
        isCoApplicant: NavigationService.isCoApplicant()
    };
};

const mapDispatchToProps = () => {
    return {
        onItemAdd: () => {
            AdditionalIncomeService.add();
        },
        onItemDelete: index => {
            AdditionalIncomeService.delete(index);
        },
        onIncomeTypeChange: (index, value) => {
            AdditionalIncomeService.typeChange(index, value);
        },
        onIncomeAmountChange: (index, value) => {
            AdditionalIncomeService.amountChange(index, value);
        },
        onDescriptionChange: (index, value) => {
            AdditionalIncomeService.descriptionChange(index, value.target.value);
        },
        onMonthChange: (index, value) => {
            AdditionalIncomeService.monthChange(index, value);
        },
        onYearChange: (index, value) => {
            AdditionalIncomeService.yearChange(index, value);
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null, {withRef: true})(injectSheet(styles)(AdditionalIncomeSection));
